import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from './Modal';

interface Customer {
  id: number;
  sfid?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  email: string;
  phone?: string | null;
  createdDate?: string | null;
}

interface EditCustomerModalProps {
  isOpen: boolean;
  customer: Customer | null;
  onClose: () => void;
  onSuccess: (updatedCustomer: Customer) => void;
  onLogout: () => void;
  showSnackbar: (message: string, type: 'success' | 'error') => void;
}

export function EditCustomerModal({ 
  isOpen, 
  customer, 
  onClose, 
  onSuccess, 
  onLogout, 
  showSnackbar 
}: EditCustomerModalProps) {
  const navigate = useNavigate();
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isUpdatingCustomer, setIsUpdatingCustomer] = useState(false);

  // Update local state when customer prop changes
  useEffect(() => {
    if (customer) {
      setEditingCustomer({ ...customer });
    }
  }, [customer]);

  const handleSaveChanges = async () => {
    if (!editingCustomer) return;
    
    // Basic validation
    if (!editingCustomer.firstName?.trim() || !editingCustomer.lastName?.trim() || !editingCustomer.email?.trim()) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!editingCustomer.email || !emailRegex.test(editingCustomer.email)) {
      showSnackbar('Please enter a valid email address', 'error');
      return;
    }
    
    setIsUpdatingCustomer(true);
    
    try {
      const response = await fetch(`/api/admin/customers/${editingCustomer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firstName: editingCustomer.firstName.trim(),
          lastName: editingCustomer.lastName.trim(),
          email: editingCustomer.email.trim(),
          phone: editingCustomer.phone?.trim() || null
        }),
      });

      if (response.status === 401) {
        onLogout();
        navigate('/login');
        return;
      }

      const result = await response.json();
      if (result.success) {
        showSnackbar('Customer updated successfully!', 'success');
        onSuccess(result.data);
        onClose();
      } else {
        showSnackbar(result.error || 'Failed to update customer', 'error');
      }
    } catch (err) {
      console.error('Update customer error:', err);
      showSnackbar(err instanceof Error ? err.message : 'Network error occurred', 'error');
    } finally {
      setIsUpdatingCustomer(false);
    }
  };

  const handleClose = () => {
    setEditingCustomer(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <h3 className="text-lg font-semibold text-gray-900 text-center mb-4">
        Edit Customer
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">First Name</label>
          <input
            type="text"
            value={editingCustomer?.firstName || ''}
            onChange={(e) => editingCustomer && setEditingCustomer({ ...editingCustomer, firstName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="First name"
            disabled={isUpdatingCustomer}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Last Name</label>
          <input
            type="text"
            value={editingCustomer?.lastName || ''}
            onChange={(e) => editingCustomer && setEditingCustomer({ ...editingCustomer, lastName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Last name"
            disabled={isUpdatingCustomer}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            value={editingCustomer?.email || ''}
            onChange={(e) => editingCustomer && setEditingCustomer({ ...editingCustomer, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="email@example.com"
            disabled={isUpdatingCustomer}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Phone</label>
          <input
            type="tel"
            value={editingCustomer?.phone || ''}
            onChange={(e) => editingCustomer && setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="(555) 123-4567"
            disabled={isUpdatingCustomer}
          />
        </div>
      </div>
      <div className="flex space-x-3 mt-6">
        <button
          onClick={handleSaveChanges}
          className="flex-1 bg-black text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer flex items-center justify-center"
          disabled={!editingCustomer?.firstName?.trim() || !editingCustomer?.lastName?.trim() || !editingCustomer?.email?.trim() || isUpdatingCustomer}
        >
          {isUpdatingCustomer ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
        <button
          onClick={handleClose}
          className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors cursor-pointer"
          disabled={isUpdatingCustomer}
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
}